/**
 * The caller's runs of one kind (own + shared with them), newest first:
 * name, status, created, owner. Re-lists while any run is still active.
 *
 * A compact two-line list rather than a table: it sits in a narrow side column,
 * where a four-column table squeezed the status badge down to "Succ…".
 */

import { useCallback, useEffect, useState } from "react";
import { Badge, Box, Group, Loader, Stack, Text, UnstyledButton } from "@mantine/core";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  RUN_POLL_MS,
  isTerminal,
  listRuns,
  type RunKind,
  type RunOut,
} from "@/shared/api/solutionRuns";
import { toastApiError } from "@/shared/ui/toast";
import { RUN_STATUS_COLOR, RUN_STATUS_LABEL, formatUtc } from "./runMeta";

interface Props {
  kind: RunKind;
  selectedRunId?: string | null;
  onSelect?: (run: RunOut) => void;
  /** Bump to force a re-list (e.g. after creating or deleting a run). */
  refreshKey?: number;
  emptyText?: string;
}

function ownerOf(r: RunOut): string {
  if (r.is_owner) return "You";
  return r.owner_display_name || r.owner_email || "—";
}

export function RunList({
  kind,
  selectedRunId,
  onSelect,
  refreshKey = 0,
  emptyText = "No runs yet.",
}: Props) {
  const getToken = useAccessToken();
  const [runs, setRuns] = useState<RunOut[] | null>(null);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      setRuns((await listRuns(token, kind)).items);
    } catch (err) {
      toastApiError("Could not list runs", err);
      setRuns((prev) => prev ?? []);
    }
  }, [getToken, kind]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const anyActive = (runs ?? []).some((r) => !isTerminal(r.status));
  useEffect(() => {
    if (!anyActive) return;
    const timer = setInterval(() => void load(), RUN_POLL_MS * 2);
    return () => clearInterval(timer);
  }, [anyActive, load]);

  if (runs === null) return <Loader size="sm" aria-label="Loading runs" />;
  if (runs.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        {emptyText}
      </Text>
    );
  }
  return (
    <Stack gap={4} data-testid="run-list">
      {runs.map((r) => {
        const selected = r.run_id === selectedRunId;
        return (
          <Box
            key={r.run_id}
            px="xs"
            py={6}
            bg={selected ? "var(--mantine-color-blue-light)" : undefined}
            // The whole row selects the run; the name stays a real button for
            // keyboard users.
            onClick={onSelect ? () => onSelect(r) : undefined}
            style={{
              borderRadius: "var(--mantine-radius-sm)",
              cursor: onSelect ? "pointer" : undefined,
            }}
            data-testid="run-row"
          >
            <Group justify="space-between" wrap="nowrap" gap="xs" align="flex-start">
              {onSelect ? (
                <UnstyledButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(r);
                  }}
                  aria-label={`Open run ${r.name}`}
                  style={{ minWidth: 0 }}
                >
                  <Text size="sm" fw={500} style={{ wordBreak: "break-word" }}>
                    {r.name}
                  </Text>
                </UnstyledButton>
              ) : (
                <Text size="sm" fw={500} style={{ minWidth: 0, wordBreak: "break-word" }}>
                  {r.name}
                </Text>
              )}
              <Badge color={RUN_STATUS_COLOR[r.status]} variant="light" size="sm">
                {RUN_STATUS_LABEL[r.status]}
              </Badge>
            </Group>
            <Text size="xs" c="dimmed" data-testid="run-meta">
              {formatUtc(r.created_at_utc)} · {ownerOf(r)}
              {!isTerminal(r.status) && r.progress.total > 0
                ? ` · ${r.progress.done}/${r.progress.total}`
                : ""}
            </Text>
          </Box>
        );
      })}
    </Stack>
  );
}
